import { ENGLISH, endingOf } from './conjugate';
import { compound, type LanguageTenses, type Tense } from './tenses';

// ─────────────────────────────────────────────────────────────────────────────
// Spanish: every tense beyond the present and preterite (which have their own
// topics). Verbs are regular in every tense here; the compound tenses also
// leave out escribir and abrir, whose participles are irregular (escrito, abierto).
// ─────────────────────────────────────────────────────────────────────────────

const REG = ['hablar', 'trabajar', 'estudiar', 'caminar', 'cocinar', 'bailar', 'cantar', 'escuchar', 'mirar',
  'comprar', 'comer', 'beber', 'aprender', 'correr', 'vender', 'comprender', 'vivir', 'escribir', 'abrir',
  'recibir', 'decidir', 'compartir'];
const REG_PP = REG.filter(v => v !== 'escribir' && v !== 'abrir');

const verbs = (list: string[]) => Object.fromEntries(list.map(v => [v, ENGLISH[v] ?? '']));
const end = (v: string) => endingOf(v)!;
const stem = (v: string) => v.slice(0, -2);

const IMPERFECT = { ar: ['aba', 'abas', 'aba', 'ábamos', 'abais', 'aban'], er: ['ía', 'ías', 'ía', 'íamos', 'íais', 'ían'] };
const FUTURE = ['é', 'ás', 'á', 'emos', 'éis', 'án'];
const COND = ['ía', 'ías', 'ía', 'íamos', 'íais', 'ían'];
const SUBJ = { ar: ['e', 'es', 'e', 'emos', 'éis', 'en'], er: ['a', 'as', 'a', 'amos', 'áis', 'an'] };
const PRET_3PL = { ar: 'aron', er: 'ieron', ir: 'ieron' };

const HABER = {
  pres: ['he', 'has', 'ha', 'hemos', 'habéis', 'han'],
  imp: ['había', 'habías', 'había', 'habíamos', 'habíais', 'habían'],
  fut: ['habré', 'habrás', 'habrá', 'habremos', 'habréis', 'habrán'],
  cond: ['habría', 'habrías', 'habría', 'habríamos', 'habríais', 'habrían'],
  pret: ['hube', 'hubiste', 'hubo', 'hubimos', 'hubisteis', 'hubieron'],
  subj: ['haya', 'hayas', 'haya', 'hayamos', 'hayáis', 'hayan'],
  subjRa: ['hubiera', 'hubieras', 'hubiera', 'hubiéramos', 'hubierais', 'hubieran'],
  subjSe: ['hubiese', 'hubieses', 'hubiese', 'hubiésemos', 'hubieseis', 'hubiesen'],
};

const participle = (v: string) => stem(v) + (end(v) === 'ar' ? 'ado' : 'ido');

/** Present subjunctive stem, with the spelling that keeps the sound: busque, llegue, empiece. */
function subjStem(v: string): string {
  const s = stem(v);
  if (end(v) !== 'ar') return s;
  if (s.endsWith('c')) return s.slice(0, -1) + 'qu';
  if (s.endsWith('g')) return s.slice(0, -1) + 'gu';
  if (s.endsWith('z')) return s.slice(0, -1) + 'c';
  return s;
}
const subjPres = (v: string, i: number) => subjStem(v) + SUBJ[end(v) === 'ar' ? 'ar' : 'er'][i];

/** hablaron → habla-; nosotros takes an accent: habláramos, comiéramos. */
function subjImp(v: string, i: number): string[] {
  const base = stem(v) + PRET_3PL[end(v)].slice(0, -3);
  const accented = base.replace(/([ae])$/, m => (m === 'a' ? 'á' : 'é'));
  const b = i === 3 ? accented : base;
  const ra = ['ra', 'ras', 'ra', 'ramos', 'rais', 'ran'][i];
  const se = ['se', 'ses', 'se', 'semos', 'seis', 'sen'][i];
  return [b + ra, b + se];
}

const subjWho = (_i: number, _a: string, who: string) => `que ${who}`;
const cmdWho = (i: number) => ['', '(tú)', '(usted)', '(nosotros)', '(vosotros)', '(ustedes)'][i];

function imperative(v: string, i: number): string[] | null {
  const e = end(v);
  switch (i) {
    case 1: return [stem(v) + (e === 'ar' ? 'a' : 'e')];          // habla, come, vive
    case 2: return [subjPres(v, 2)];                                 // hable
    case 3: return [subjPres(v, 3)];                                 // hablemos
    case 4: return [v.slice(0, -1) + 'd'];                           // hablad
    case 5: return [subjPres(v, 5)];                                 // hablen
    default: return null;
  }
}

const TENSES: Tense[] = [
  { id: 'imperfecto', title: 'Imperfect', blurb: 'hablaba, comías, vivíamos…', verbs: verbs(REG),
    forms: (v, i) => [stem(v) + IMPERFECT[end(v) === 'ar' ? 'ar' : 'er'][i]] },
  { id: 'futuro', title: 'Future', blurb: 'hablaré, comerás, vivirán…', verbs: verbs(REG),
    forms: (v, i) => [v + FUTURE[i]] },
  { id: 'condicional', title: 'Conditional', blurb: 'hablaría, comerías…', verbs: verbs(REG),
    forms: (v, i) => [v + COND[i]] },
  { id: 'perfecto', title: 'Present perfect', blurb: 'he hablado, has comido…', verbs: verbs(REG_PP),
    forms: (v, i) => compound(HABER.pres[i], participle(v)) },
  { id: 'pluscuamperfecto', title: 'Past perfect (pluperfect)', blurb: 'había hablado…', verbs: verbs(REG_PP),
    forms: (v, i) => compound(HABER.imp[i], participle(v)) },
  { id: 'futuro_perfecto', title: 'Future perfect', blurb: 'habré hablado…', verbs: verbs(REG_PP),
    forms: (v, i) => compound(HABER.fut[i], participle(v)) },
  { id: 'condicional_perfecto', title: 'Conditional perfect', blurb: 'habría hablado…', verbs: verbs(REG_PP),
    forms: (v, i) => compound(HABER.cond[i], participle(v)) },
  { id: 'preterito_anterior', title: 'Preterite perfect (literary)', blurb: 'hube hablado…', verbs: verbs(REG_PP),
    forms: (v, i) => compound(HABER.pret[i], participle(v)) },
  { id: 'subj_presente', title: 'Present subjunctive', blurb: 'que hable, que comas…', verbs: verbs(REG),
    forms: (v, i) => [subjPres(v, i)], who: subjWho },
  { id: 'subj_imperfecto', title: 'Imperfect subjunctive', blurb: 'que hablara / hablase…', verbs: verbs(REG),
    forms: subjImp, who: subjWho },
  { id: 'subj_perfecto', title: 'Present perfect subjunctive', blurb: 'que haya hablado…', verbs: verbs(REG_PP),
    forms: (v, i) => compound(HABER.subj[i], participle(v)), who: subjWho },
  { id: 'subj_pluscuamperfecto', title: 'Pluperfect subjunctive', blurb: 'que hubiera hablado…', verbs: verbs(REG_PP),
    forms: (v, i) => [...compound(HABER.subjRa[i], participle(v)), ...compound(HABER.subjSe[i], participle(v))], who: subjWho },
  { id: 'imperativo', title: 'Commands (affirmative)', blurb: '¡habla!, ¡coma!, ¡vivamos!…', verbs: verbs(REG),
    people: [1, 2, 3, 4, 5], forms: imperative, who: cmdWho },
  { id: 'imperativo_neg', title: 'Commands (negative)', blurb: 'no hables, no coman…', verbs: verbs(REG),
    people: [1, 2, 3, 4, 5], forms: (v, i) => [subjPres(v, i)], who: i => `${cmdWho(i)} no` },
];

export const ES_TENSES: LanguageTenses = {
  lang: 'es',
  people: [['yo'], ['tú'], ['él', 'ella', 'usted'], ['nosotros'], ['vosotros'], ['ellos', 'ellas', 'ustedes']],
  optional: 4,
  tenses: TENSES,
};
